import {
  BufferGeometry,
  Group,
  InstancedMesh,
  Matrix4,
  Object3D,
  Quaternion,
  Vector3,
  type Material,
} from 'three'
import type { ColorType } from '../core/enums'
import { convertPosition, convertQuaternion } from '../core/unity'
import type { AssetLibrary, ManifestNode } from './assetLibrary'
import { characterMaterialName } from './palette'

/**
 * Everyone in the game who is not a bus.
 *
 * The art is the `Stickman` prefab: one active child, `Char (1)`, carrying the
 * `Char` mesh at scale 0.4576334 with `Char_Pink` on it, plus an inactive
 * `charMesh_0` (the rigged version the shipped game does not draw — every bit
 * of motion is DOTween on the transform, see `characterAnim.ts`).
 *
 * Colour works exactly as it does for a bus, and the same way the original
 * does it: `Character.SwitchColor` swaps the renderer's material, and the real
 * colour lives in that material's `_VertexColor*Color` blended against the
 * mesh's vertex mask. The blend happens in the shader, so every colour shares
 * one geometry and differs only by material — this keeps **one batch per
 * colour**, each an `InstancedMesh`. Twelve draw calls covers every passenger
 * on screen: queues, waiting areas, boarding hops and riders on the roof.
 *
 * Usage is per frame: `begin()`, `add(...)` for everyone visible, `end()`.
 * Positions and rotations go in as **Unity space** — the same space the
 * simulation works in — and are converted here.
 */

/** `Stickman` → `Char (1)`, if the prefab is missing for some reason. */
const FALLBACK_PREFAB_SCALE = 0.4576334

interface Batch {
  material: Material
  mesh: InstancedMesh | null
  used: number
}

const _matrix = new Matrix4()
const _position = new Vector3()
const _quaternion = new Quaternion()
const _scale = new Vector3()

export class CharacterView {
  readonly object = new Group()

  private readonly batches = new Map<ColorType, Batch>()
  private readonly source: BufferGeometry | null
  /** Uniform scale baked into the prefab, before `queueCharacterScale`. */
  readonly prefabScale: number

  constructor(private library: AssetLibrary) {
    this.object.name = 'characters'
    this.source = library.peekMesh('Char')
    this.prefabScale = readPrefabScale(library.prefab('Stickman'))
  }

  /** Meshes to prefetch before the first frame. */
  static requiredMeshes(library: AssetLibrary): string[] {
    const node = library.prefab('Stickman')
    return node ? [...library.meshesUsedBy(node)] : ['Char']
  }

  get ready(): boolean {
    return this.source !== null
  }

  begin(): void {
    for (const batch of this.batches.values()) batch.used = 0
  }

  /**
   * Draw one passenger.
   *
   * `unityPosition` is the character's feet — `Char.obj` has its origin on the
   * ground (y spans -0.013 to 5.07), which is why nothing here offsets by half
   * a height the way the placeholder cylinders had to.
   */
  add(color: ColorType, unityPosition: Vector3, unityRotation: Quaternion, scale: number): void {
    const batch = this.batchFor(color)
    if (!batch) return

    convertPosition(unityPosition, _position)
    convertQuaternion(unityRotation, _quaternion)
    _scale.setScalar(scale)
    _matrix.compose(_position, _quaternion, _scale)

    const index = batch.used++
    const mesh = this.ensureCapacity(batch, index + 1)
    if (!mesh) return
    mesh.setMatrixAt(index, _matrix)
  }

  end(): void {
    for (const batch of this.batches.values()) {
      const mesh = batch.mesh
      if (!mesh) continue
      mesh.count = batch.used
      mesh.visible = batch.used > 0
      mesh.instanceMatrix.needsUpdate = true
      // The instances move every frame and can be anywhere in the level, so a
      // per-instance bounding volume is not worth computing; skip the cull.
      mesh.frustumCulled = false
    }
  }

  private batchFor(color: ColorType): Batch | null {
    const existing = this.batches.get(color)
    if (existing) return existing
    if (!this.source) return null

    // One material per colour over one shared geometry. `Char_<Colour>` is
    // white over the same `CharTex` in every variant; what differs is the
    // palette the shader blends against the mesh's mask.
    const material = this.library.material(characterMaterialName(color))
    const batch: Batch = { material, mesh: null, used: 0 }
    this.batches.set(color, batch)
    return batch
  }

  private ensureCapacity(batch: Batch, needed: number): InstancedMesh | null {
    if (!this.source) return null
    if (batch.mesh && batch.mesh.instanceMatrix.count >= needed) return batch.mesh

    const capacity = Math.max(16, nextPowerOfTwo(needed))
    const replacement = new InstancedMesh(this.source, batch.material, capacity)
    replacement.name = 'characters#batch'
    replacement.frustumCulled = false
    // 📐 `Stickman.prefab` `Char (1)` `m_CastShadows: 1`, and no `Char_*.mat`
    // disables SHADOWCASTER, so every passenger casts. Not through
    // `applyShadowCasting`: the batch is built here from one geometry, not
    // from a prefab node.
    replacement.castShadow = true

    const previous = batch.mesh
    if (previous) {
      // Copy the whole instance buffer, not `previous.count` entries of it.
      // `count` is *last* frame's total, written by `end()`; the matrices this
      // frame has already written live at indices `0 .. needed - 2`, which runs
      // past `count` whenever a batch grows beyond its capacity within a single
      // frame — the waiting area filling up is exactly that. Copying `count`
      // dropped the remainder, leaving those instances with an all-zero matrix:
      // a degenerate, invisible body. Every passenger of that colour between
      // last frame's total and the growth point disappeared for one frame,
      // which is the flicker seen while the crowd walks in.
      replacement.instanceMatrix.array.set(previous.instanceMatrix.array)
      previous.removeFromParent()
      previous.dispose()
    }

    batch.mesh = replacement
    this.object.add(replacement)
    return replacement
  }

  dispose(): void {
    for (const batch of this.batches.values()) {
      batch.mesh?.removeFromParent()
      // The geometry is shared and owned by the library; only the instance
      // buffers belong to this view.
      batch.mesh?.dispose()
    }
    this.batches.clear()
    this.object.removeFromParent()
  }
}

function nextPowerOfTwo(n: number): number {
  let p = 1
  while (p < n) p *= 2
  return p
}

/** The uniform scale on `Stickman`'s only active child. */
function readPrefabScale(node: ManifestNode | null): number {
  if (!node) return FALLBACK_PREFAB_SCALE
  for (const child of node.children) {
    if (child.active && child.mesh === 'Char') return child.scale.x
  }
  return FALLBACK_PREFAB_SCALE
}

/** Somewhere to hang a character that has no `Object3D` of its own. */
export type CharacterHost = Object3D
